import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import { PanelBody, SelectControl, ComboboxControl, TextControl, __experimentalNumberControl as NumberControl, Placeholder, Spinner } from '@wordpress/components';
import { useState, useEffect } from '@wordpress/element';
import useStyles from '../../js/hooks/useStyles';
import { casFetch } from '../../js/casFetch';

export default function Edit({ attributes, setAttributes }) {
    const { projectToplistId, style, ctaText, numberOfItems, title } = attributes;
    const [toplists, setToplists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Fetch available styles for toplist blocks
    const { styles: styleOptions, loading: stylesLoading } = useStyles('toplist');

    // Fetch project toplists with full data for preview
    useEffect(() => {
        casFetch('/wp-json/cas/v1/project-toplists')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch toplists');
                }
                return response.json();
            })
            .then(data => {
                if (data.success && Array.isArray(data.data)) {
                    setToplists(data.data);
                    // console.log('Fetched toplists:', data.data);
                } else {
                    setError('No toplists available');
                }
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    // Get the selected toplist data for preview
    const selectedToplist = toplists.find(toplist => toplist.uuid === projectToplistId);

    const blockProps = useBlockProps({
        className: 'wp-block-cas-project-toplist'
    });

    // Every branch below returns the same shape: the canvas div, then the
    // inspector. React reconciles a component by the type of the element it
    // returns, so returning a bare <div> while loading and a <> once loaded
    // tears the whole block down and rebuilds it the instant the fetch
    // resolves. Anything the editor had already measured against the old
    // node is then left holding a detached one, which surfaces inside
    // @wordpress/components as getComputedStyle receiving a non-Element.
    // It only bites when the request lands after the editor's post-insert
    // work - fast locally, slow on a remote site - which is why this
    // reproduced on staging and not in development.
    const styleInspector = (
        <InspectorControls>
            <PanelBody title={__('Toplist Settings', 'cas-plugin')} className="cas-block-inspector">
                <SelectControl
                    label={__('Style', 'cas-plugin')}
                    value={style}
                    options={styleOptions}
                    onChange={(value) => setAttributes({ style: value })}
                    disabled={stylesLoading}
                    help={stylesLoading ? __('Loading available styles...', 'cas-plugin') : __('Choose a style template for this toplist.', 'cas-plugin')}
                />
            </PanelBody>
        </InspectorControls>
    );

    if (loading) {
        return (
            <>
                <div {...blockProps}>
                    <Placeholder 
                        icon="list-view" 
                        label={__('CAS Project Toplist', 'cas-plugin')}
                        className="cas-block-placeholder"
                    >
                        <Spinner />
                        <p>{__('Loading toplists...', 'cas-plugin')}</p>
                    </Placeholder>
                </div>
                {styleInspector}
            </>
        );
    }

    if (error) {
        return (
            <>
                <div {...blockProps}>
                    <Placeholder 
                        icon="warning" 
                        label={__('CAS Project Toplist', 'cas-plugin')}
                        className="cas-block-placeholder"
                    >
                        <p>{__('Error loading toplists: ', 'cas-plugin') + error}</p>
                    </Placeholder>
                </div>
                {styleInspector}
            </>
        );
    }

    if (!projectToplistId) {
        return (
            <>
                <div {...blockProps}>
                    <Placeholder 
                        icon="list-view" 
                        label={__('CAS Project Toplist', 'cas-plugin')}
                        instructions={__('Search and select a project toplist to display', 'cas-plugin')}
                        className="cas-block-placeholder"
                    >
                        <ComboboxControl
                            label={__('Search Project Toplists', 'cas-plugin')}
                            hideLabelFromVision
                            value={projectToplistId || null}
                            options={toplists.map(toplist => ({
                                label: `${toplist.name || 'Unnamed Toplist'}${toplist.market_name ? ` (${toplist.market_name})` : ''}`,
                                value: toplist.uuid
                            }))}
                            onChange={(value) => {
                                setAttributes({
                                    projectToplistId: value
                                });
                            }}
                            placeholder={__('Type to search toplists...', 'cas-plugin')}
                        />
                    </Placeholder>
                </div>
                {styleInspector}
            </>
        );
    }

    // Client-side preview with real data - using offer-style cards for each listing!
    return (
        <>
            <div {...blockProps}>
                {selectedToplist ? (
                    <div className="cas-toplist-container">
                        <div className="cas-toplist-header">
                            <div className="cas-toplist-name">
                                {selectedToplist.name || 'Casino Toplist'}
                            </div>
                        </div>
                        <div className="cas-toplist-cards">
                            {selectedToplist.listings && selectedToplist.listings.length > 0 ? (
                                selectedToplist.listings.map((listing, index) => {
                                    const isGhosted = numberOfItems > 0 && index >= numberOfItems;
                                    
                                    return (
                                        <div 
                                            key={index} 
                                            className="cas-offer-skeleton"
                                            style={isGhosted ? { 
                                                opacity: 0.3,
                                                filter: 'grayscale(100%)'
                                            } : {}}
                                        >
                                            <div className="cas-toplist-rank">#{index + 1}</div>
                                            <div 
                                                className="cas-logo-placeholder"
                                                style={{ 
                                                    backgroundColor: listing.brand_logo_background_color || '#f8f9fa'
                                                }}
                                            >
                                                {listing.brand_logo_url ? (
                                                    <img 
                                                        src={listing.brand_logo_url} 
                                                        alt={listing.brand_name || 'Brand Logo'}
                                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                    />
                                                ) : (
                                                    <span style={{ fontSize: '10px', color: '#6c757d' }}>LOGO</span>
                                                )}
                                            </div>
                                            <div className="cas-offer-content">
                                                <div 
                                                    className="cas-offer-title"
                                                    dangerouslySetInnerHTML={{
                                                        __html: listing.offer_title_highlighted || listing.offer_title || 'Special Offer'
                                                    }}
                                                />
                                            </div>
                                            <div className="cas-cta-placeholder">
                                                {ctaText || listing.cta_text || 'Get Offer'}
                                            </div>
                                            <div 
                                                className="cas-offer-terms"
                                                dangerouslySetInnerHTML={{
                                                    __html: listing.offer_terms || 'T&Cs apply. 18+. Gamble responsibly.'
                                                }}
                                            />
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="cas-offer-skeleton">
                                    <div className="cas-offer-content">
                                        <div className="cas-offer-title">No listings available</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <Placeholder 
                        icon="warning" 
                        label={__('CAS Project Toplist', 'cas-plugin')}
                        className="cas-block-placeholder"
                    >
                        <p>{__('Selected toplist not found. Please choose a different toplist.', 'cas-plugin')}</p>
                    </Placeholder>
                )}
            </div>
            <InspectorControls>
                <PanelBody title={__('Toplist Settings', 'cas-plugin')} className="cas-block-inspector">
                    <TextControl
                        label={__('Title', 'cas-plugin')}
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                        placeholder={__('Optional heading', 'cas-plugin')}
                        help={__('Add an optional H2 heading above the toplist.', 'cas-plugin')}
                    />
                    <ComboboxControl
                        label={__('Search Project Toplists', 'cas-plugin')}
                        value={projectToplistId || null}
                        options={toplists.map(toplist => ({
                            label: `${toplist.name || 'Unnamed Toplist'}${toplist.market_name ? ` (${toplist.market_name})` : ''}`,
                            value: toplist.uuid
                        }))}
                        onChange={(value) => {
                            setAttributes({
                                projectToplistId: value
                            });
                        }}
                        allowReset={true}
                        placeholder={__('Type to search toplists...', 'cas-plugin')}
                        help={__('Search by toplist name or market to quickly find the toplist you want to display.', 'cas-plugin')}
                    />
                    <SelectControl
                        label={__('Style', 'cas-plugin')}
                        value={style}
                        options={styleOptions}
                        onChange={(value) => setAttributes({ style: value })}
                        disabled={stylesLoading}
                        help={stylesLoading ? __('Loading available styles...', 'cas-plugin') : __('Choose a style template for this toplist.', 'cas-plugin')}
                    />
                    <TextControl
                        label={__('Custom CTA Text', 'cas-plugin')}
                        value={ctaText}
                        onChange={(value) => setAttributes({ ctaText: value })}
                        placeholder={__('Leave empty to use default', 'cas-plugin')}
                        help={__('Override the call-to-action button text. Leave empty to use the default text from the toplist settings.', 'cas-plugin')}
                    />
                    <NumberControl
                        label={__('Number of Items', 'cas-plugin')}
                        value={numberOfItems}
                        onChange={(value) => setAttributes({ numberOfItems: parseInt(value) || 0 })}
                        min={1}
                        placeholder={__('All items', 'cas-plugin')}
                        help={__('Limit the number of items displayed. Leave empty to show all items.', 'cas-plugin')}
                    />
                </PanelBody>
            </InspectorControls>
        </>
    );
}
